/* ========== COLOR RESOLVER ==========
 * Resolves any OOXML fill (a:solidFill, a:srgbClr, a:schemeClr) to a hex
 * color string, applying Excel's lumMod/lumOff luminance transforms.
 *
 * The workbook's theme colors are set via setThemeColors() before parsing
 * begins, so schemeClr (accent1..accent6) can resolve to actual hex.
 * ==================================== */

import { qLocal, qDirectChild } from "../xmlUtils";
import { THEME_COLOR_MAP } from "./constants";

// Workbook theme colors (hex strings without #, like "4472C4"). The
// extractor sets this once at the start of each parse run.
let parserThemeColors = null;

export function setThemeColors(themeColors) {
  parserThemeColors = themeColors || null;
}

export function getThemeColors() {
  return parserThemeColors;
}

/**
 * The workbook's accent colors (accent1..accent6) as a `#rrggbb` array —
 * this is the palette Excel cycles through for automatic series colors.
 *
 * Charts that don't author an explicit per-series fill rely on it, so
 * using it (instead of a hardcoded Office palette) is what makes a chart
 * from a non-default-theme workbook render in the RIGHT colours.  Returns
 * null when no theme is loaded, so callers can fall back safely.
 */
export function getThemeSeriesPalette() {
  const tc = parserThemeColors;
  if (!tc) return null;
  const accents = [];
  // accent1..accent6 live at theme indices 4..9 (see THEME_COLOR_MAP).
  for (let i = 4; i <= 9; i += 1) {
    if (tc[i]) accents.push("#" + String(tc[i]).replace(/^#/, ""));
  }
  return accents.length ? accents : null;
}

/**
 * Apply Excel's lumMod (luminance multiplier) and lumOff (luminance offset)
 * to a hex color. Both values are 0-100000 representing 0-1.0.
 *
 * Excel's true formula is HSL-based; this approximation matches the chart-
 * styling cases (35%/60%/80% accent shading) closely enough that the eye
 * can't tell the difference from Excel.
 */
export function applyLum(hex, lumMod, lumOff) {
  if (!hex) return hex;
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);

  // RGB → HSL
  const rN = r / 255, gN = g / 255, bN = b / 255;
  const max = Math.max(rN, gN, bN), min = Math.min(rN, gN, bN);
  let hH = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rN: hH = (gN - bN) / d + (gN < bN ? 6 : 0); break;
      case gN: hH = (bN - rN) / d + 2; break;
      case bN: hH = (rN - gN) / d + 4; break;
      default: hH = 0;
    }
    hH /= 6;
  }

  const mod = lumMod != null ? lumMod / 100000 : 1;
  const off = lumOff != null ? lumOff / 100000 : 0;
  let lNew = l * mod + off;
  lNew = Math.max(0, Math.min(1, lNew));

  // HSL → RGB
  function hue2rgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }
  let rR, gG, bB;
  if (s === 0) {
    rR = gG = bB = lNew;
  } else {
    const q = lNew < 0.5 ? lNew * (1 + s) : lNew + s - lNew * s;
    const p = 2 * lNew - q;
    rR = hue2rgb(p, q, hH + 1 / 3);
    gG = hue2rgb(p, q, hH);
    bB = hue2rgb(p, q, hH - 1 / 3);
  }
  const toHex = (v) => Math.round(v * 255).toString(16).padStart(2, '0');
  return '#' + toHex(rR) + toHex(gG) + toHex(bB);
}

/**
 * Resolve an <a:solidFill> node to a hex color string.
 * Handles srgbClr (literal hex) and schemeClr (theme accent + lumMod/lumOff).
 * Returns null if the node can't be resolved (e.g. no theme loaded).
 */
export function resolveFillToHex(fillNode) {
  if (!fillNode) return null;

  // Literal RGB
  const srgb = qDirectChild(fillNode, "srgbClr") || qLocal(fillNode, "srgbClr");
  if (srgb) {
    const hex = srgb.getAttribute("val");
    if (hex) {
      let color = "#" + hex;
      const lumMod = qLocal(srgb, "lumMod");
      const lumOff = qLocal(srgb, "lumOff");
      if (lumMod || lumOff) {
        color = applyLum(
          color,
          lumMod ? parseInt(lumMod.getAttribute("val"), 10) : null,
          lumOff ? parseInt(lumOff.getAttribute("val"), 10) : null
        );
      }
      return color;
    }
  }

  // Theme color
  const schemeClr = qDirectChild(fillNode, "schemeClr") || qLocal(fillNode, "schemeClr");
  if (schemeClr && parserThemeColors) {
    const schemeName = schemeClr.getAttribute("val");
    const themeIdx = THEME_COLOR_MAP[schemeName];
    if (themeIdx != null && parserThemeColors[themeIdx]) {
      let color = "#" + parserThemeColors[themeIdx];
      const lumMod = qLocal(schemeClr, "lumMod");
      const lumOff = qLocal(schemeClr, "lumOff");
      if (lumMod || lumOff) {
        color = applyLum(
          color,
          lumMod ? parseInt(lumMod.getAttribute("val"), 10) : null,
          lumOff ? parseInt(lumOff.getAttribute("val"), 10) : null
        );
      }
      return color;
    }
  }

  return null;
}

/**
 * Resolve a series/dPt <c:spPr> node to a hex color.
 *
 * Priority matters: pie/doughnut/bar/area put the fill color in the
 * direct top-level <a:solidFill> and the WHITE BORDER in
 * <a:ln><a:solidFill>. Line series do the opposite — no top-level fill,
 * color is in ln > solidFill.
 *
 * If we read ln first we'd return the white border for pie slices and
 * the chart would appear blank. So: direct fill first, line fallback.
 *
 * We deliberately use qDirectChild (not qLocal which does deep descent)
 * because qLocal would happily return the ln's nested solidFill when
 * looking for the top-level one.
 */
export function resolveSpPrColor(spPr) {
  if (!spPr) return null;

  // 1. Top-level fill (pie / doughnut / dPt / bar / area / column)
  const directFill = qDirectChild(spPr, "solidFill");
  if (directFill) {
    const c = resolveFillToHex(directFill);
    if (c) return c;
  }

  // 2. Line color (line / scatter / radar series whose spPr has no top-level fill)
  const lnNode = qDirectChild(spPr, "ln");
  if (lnNode) {
    const lnFill = qDirectChild(lnNode, "solidFill");
    return resolveFillToHex(lnFill);
  }

  return null;
}
